import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import SkeletonText from "components/ui/skeleton/SkeletonText";
import Tooltip from "components/ui/tooltip/Tooltip";
import {
  ACCOUNT_PROVIDER_LABELS,
  AccountProviderKind,
  accountProviderKind,
} from "lib/auth/accountProviders";
import { BetterAuthAccount, useListAccounts } from "lib/hooks/api/auth/useListAccounts";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { formatDate } from "lib/utils";

const PROVIDER_ICONS: Record<AccountProviderKind, IconProps["icon"]> = {
  credential: "mail01",
  google: "google",
  microsoft: "microsoft",
  sso: "building07",
};

function AccountRow({ account }: { account: BetterAuthAccount }) {
  const kind = accountProviderKind(account.providerId);
  return (
    <Row aria-label={ACCOUNT_PROVIDER_LABELS[kind]}>
      <UIcon icon={PROVIDER_ICONS[kind]} size={20} />
      <Flex column>
        {/* The provider's account ID is surfaced on hover to help debug sign-in issues. */}
        <Tooltip content={`Account ID: ${account.accountId}`}>
          <Text bold>{ACCOUNT_PROVIDER_LABELS[kind]}</Text>
        </Tooltip>
        <Text description color="sysTextSecondary">
          Connected on {formatDate(account.createdAt)}
        </Text>
      </Flex>
    </Row>
  );
}

/** The ways the user can sign in to Stotles. */
function ConnectedAccountsSection(): JSX.Element {
  const { data: accounts, isLoading, isError } = useListAccounts();

  if (isLoading) {
    return (
      <Flex column gap={8} aria-label="Loading sign-in methods">
        <SkeletonText width="240px" />
        <SkeletonText width="240px" />
      </Flex>
    );
  }

  // Every user has at least one sign-in method, so an empty list means something is broken.
  if (isError || !accounts?.length) {
    return (
      <Text color="sysDestructiveDefault">
        We couldn't load your sign-in methods. Please refresh the page to try again.
      </Text>
    );
  }

  return (
    <List>
      {accounts.map((account) => (
        <AccountRow key={account.id} account={account} />
      ))}
    </List>
  );
}

export default ConnectedAccountsSection;

const List = styled.ul(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
}));

const Row = styled.li(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 16px",
  "&:not(:last-of-type)": {
    borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  },
}));
